import dayjs from 'dayjs';
import _, { isEmpty } from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import { withRouter } from 'react-router-dom';
import CustomHeaderCell from 'shared/PitstopUI/PitstopTable/CustomHeaderCell';
import styled from 'styled-components';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  notification,
  Popover,
  Radio,
  Row,
  Select,
  Spin,
} from 'antd';

import {
  CarStore,
  CurrentUserStore,
  IssueStore,
  PitstopCookie,
  ShopStore,
  TableStore,
} from 'stores';
import { IssuesTableCacheStore } from 'stores/CacheStore';

import CreateWorkOrderFromIssuesBtn from 'containers/IssueProfile/CreateWorkOrderFromIssuesBtn';
import MarkAsCompleteBtn from 'containers/IssueProfile/MarkAsCompleteBtn';
import MarkAsCurrentBtn from 'containers/IssueProfile/MarkAsCurrentBtn';

import { convertSource, getColumnsBySource } from 'components/Issue/utils';
import { queryUtils } from 'helpers';
import { customFleets, PitstopButton, PitstopTable } from 'shared';

import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { SearchIssue } from 'components/Issue';
import deviceStore from 'stores/Classes/DeviceStore';
import PrintService from './PrintService';

dayjs.extend(utc);
dayjs.extend(timezone);

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;
const Option = Select.Option;
const { RangePicker } = DatePicker;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FilterCountBadge = styled.span`
  background-color: #ffffff;
  color: #0038b3;
  border-radius: 10px;
  padding: 0 6px;
  margin-left: 6px;
  font-size: 13px;
`;

const LabelSpan = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  display: block;
`;

const FilterItemWrapper = styled.div`
  margin-top: 13px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 13px;

  button {
    margin-left: 10px;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;

  .filter-button {
    margin-right: 10px;
  }

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 8px;
  }
`;

const DownloadReportWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
  top: 13px;
  z-index: 4;

  .download-button {
    margin-right: 10px;
  }

  @media (max-width: 768px) {
    top: 51px;
    right: 10px;
    align-items: flex-start;
    background: #fff;
  }
`;

const PDFButton = styled(Button)`
  color: #333;
  padding-left: 0;
`;

const ActionsWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;

  button {
    margin-right: 10px;
  }
`;

class IssuesTable extends Component {
  static propTypes = {
    refs: PropTypes.any,
    car: PropTypes.object,
    history: PropTypes.object,
    isEnabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    fetchData: PropTypes.func.isRequired,
    limit: PropTypes.number,
    printedComponentRef: PropTypes.any,
    downloadCsv: PropTypes.func,
    location: PropTypes.object,
    page: PropTypes.string,
    onIssuesListChange: PropTypes.func,
    reload: PropTypes.bool,
  };

  static defaultProps = {
    isEnabled: true,
    limit: 20,
    page: null,
  };

  getPriority = () =>
    queryUtils.getSearchParamByKey(window.location.search, 'priorityFilter');

  getIssueStatus = () =>
    queryUtils.getSearchParamByKey(window.location.search, 'issueStatusFilter');

  getSource = () =>
    queryUtils.getSearchParamByKey(window.location.search, 'source');

  getFilterDirectory() {
    return window.location.pathname + 'Filters';
  }

  state = {
    searchValue: '',
    searchOn: [],
    status: 'new',
    source: this.getSource() || 'all',
    priority: this.getPriority() || 'all',
    isReloaded: false,
    isAllToOne: true,
    startDate: null,
    endDate: null,
    allInOneSelectedIssues: [],
    processingDownloadCsv: false,
    filtersControl: [],
    filterPopoverVisible: false,

    // filter values
    filterValues: {
      status: 'new',
      source: this.getSource() || 'all',
      priority: this.getPriority() || 'all',
      isAllToOne: true,
      date: [],
    },
  };

  dtcTableStore = new TableStore();
  recallsTableStore = new TableStore();
  routineTableStore = new TableStore();
  algorithmTableStore = new TableStore();
  userTableStore = new TableStore();
  archiveTableStore = new TableStore();

  allIssuesTableStore = new TableStore();

  disposer = observe(ShopStore.currentShop, 'id', () => {
    this.setIsAllToOne();
  });

  constructor(props) {
    super(props);
    this.onRefresh = this.onRefresh.bind(this);
    this.downloadCsv = this.downloadCsv.bind(this);
    this.printedComponentRef = createRef();
    this.issuesTableref = createRef();
  }

  get allTableStores() {
    return [
      {
        key: 'dtc',
        title: 'Engine Fault Codes Issues',
        source: 'dtc',
        store: this.dtcTableStore,
        setStore: (store) => (this.dtcTableStore = store),
      },
      {
        key: 'algorithm',
        title: 'Pitstop Suggested Issues',
        source: 'algorithm',
        store: this.algorithmTableStore,
        setStore: (store) => (this.algorithmTableStore = store),
      },
      {
        key: 'routine',
        title: 'Routine Based Services',
        source: 'routine',
        store: this.routineTableStore,
        setStore: (store) => (this.routineTableStore = store),
      },
      {
        key: 'recall',
        title: 'Recall Services',
        source: 'recall',
        store: this.recallsTableStore,
        setStore: (store) => (this.recallsTableStore = store),
      },
      {
        key: 'user',
        title: 'User Issues',
        source: 'user',
        store: this.userTableStore,
        setStore: (store) => (this.userTableStore = store),
      },
      {
        key: 'archive',
        title: 'Service Center',
        source: 'archive',
        store: this.archiveTableStore,
        setStore: (store) => (this.archiveTableStore = store),
      },
    ];
  }

  get issueStatusCache() {
    return this.state.status;
  }

  get issuePriorityCache() {
    return this.state.priority;
  }

  get issueSourceCache() {
    const source = this.state.source;

    return source;
  }

  get allTableSources() {
    const source = convertSource(this.issueSourceCache);

    if (source === 'all') return this.allTableStores;

    return _.filter(this.allTableStores, (table) => table.source === source);
  }

  get isAllStoresLoaded() {
    if (this.state.isAllToOne) return this.allIssuesTableStore?.loaded;

    const tables = _.filter(
      this.allTableSources,
      (table) => table.store.loaded === true
    );

    return tables.length === this.allTableSources.length;
  }

  get isAllStoresEmpty() {
    if (this.state.isAllToOne) return _.isEmpty(this.allIssuesTableStore?.data);

    const tables = _.filter(this.allTableSources, (table) =>
      _.isEmpty(table.store.data)
    );

    return tables.length === this.allTableSources.length;
  }

  componentDidMount() {
    if (typeof this.props.refs === 'function') {
      this.props.refs(this);
    }
    this.setIsAllToOne();

    // load filters from local storage
    const filters = localStorage.getItem(this.getFilterDirectory());

    if (filters) {
      this.setState({ filterValues: JSON.parse(filters) }, () => {
        this.applyFilters();
      });
    }

    //Workaround for Ferrovial defaulting to isAllToOne
    this.onCheck({ target: { checked: true } });

    // Apply the initial priority filter from URL
    const priorityFromUrl = this.getPriority();
    const statusFromUrl = this.getIssueStatus();
    if (priorityFromUrl) {
      this.setState((prevState) => ({
        ...prevState,
        priority: priorityFromUrl,
        status: statusFromUrl,
        filterValues: {
          ...prevState.filterValues,
          priority: priorityFromUrl,
          status: statusFromUrl,
        },
      }));
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.reload !== prevProps.reload && this.props.reload) {
      this.refreshVehiclesList();
    }
  }

  componentWillUnmount() {
    this.disposer();
  }

  setIsAllToOne = () => {
    if (_.includes(customFleets.renzi, ShopStore.currentShop.id)) {
      this.setState(
        {
          isAllToOne: true,
        },
        () => PitstopCookie.set('isAllToOne', true)
      );
    }
  };

  reloadTable = async () => {
    this.setState({
      filterValues: this.state.filterValues,
    });
  };

  getDataBySource = (source) => {
    const { isAllToOne } = this.state;
    const table = _.filter(
      this.allTableStores,
      (table) => table.source === source
    )[0];

    const data = isAllToOne ? this.allIssuesTableStore.data : table.store.data;

    return _.map(data, (id) => {
      const issue = IssueStore.data.get(id);

      if (!issue) return;

      return {
        ...issue,
        key: id,
        name: issue.getName(),
        priorityTagColor: issue.priorityTagColor,
        priorityCategory: issue.priorityCategory,
      };
    });
  };

  onCheck = (e) => {
    if (e.target.checked) {
      this.addFilterToControl('isAllToOne');
    } else {
      this.removeFilterFromControl('isAllToOne');
    }
    this.setState(
      {
        isAllToOne: e.target.checked,
        allInOneSelectedIssues: [],
      },
      () => {
        if (e.target.checked) {
          PitstopCookie.set('isAllToOne', true);
        } else {
          PitstopCookie.remove('isAllToOne');
        }
      }
    );
  };

  // update the filter value
  onChangeFilter = (stateKeyToChange, value) => {
    this.setState({
      filterValues: {
        ...this.state.filterValues,
        [stateKeyToChange]: value,
      },
    });
  };

  applyFilters = () => {
    const { filterValues } = this.state;
    const { status, source, priority, date } = filterValues;

    const newFiltersControl = [];
    if (status !== 'new') {
      newFiltersControl.push('status');
    }
    if (source !== 'all') {
      newFiltersControl.push('source');
    }
    if (priority !== 'all') {
      newFiltersControl.push('priority');
    }
    if (date && date.length) {
      newFiltersControl.push('date');
    }

    const startDate =
      date && date.length && date[0]
        ? moment
            .tz(date[0], 'YYYY-MM-DD', CurrentUserStore.user.settings.timezone)
            .startOf('day')
            .toISOString()
        : null;
    const endDate =
      date && date.length && date[1]
        ? moment
            .tz(date[1], 'YYYY-MM-DD', CurrentUserStore.user.settings.timezone)
            .endOf('day')
            .toISOString()
        : null;

    this.setState(
      {
        status,
        source: _.isNil(source) ? 'all' : source,
        priority,
        startDate,
        endDate,
        allInOneSelectedIssues: [],
        filterPopoverVisible: false,
        filtersControl: newFiltersControl,
      },
      () => {
        // remove or add item to filter control
        if (this.state.status === 'new') {
          this.removeFilterFromControl('status');
        } else {
          this.addFilterToControl('status');
        }
        if (this.state.source === 'all') {
          this.removeFilterFromControl('source');
        } else {
          this.addFilterToControl('source');
        }
        if (this.state.priority === 'all') {
          this.removeFilterFromControl('priority');
        } else {
          this.addFilterToControl('priority');
        }
        if (_.isEmpty(date)) {
          this.removeFilterFromControl('date');
        } else {
          this.addFilterToControl('date');
        }
        IssuesTableCacheStore.setData('source', this.state.source);
        IssuesTableCacheStore.setData('priority', this.state.priority);
        IssuesTableCacheStore.setData('status', this.state.status);

        // save filters to local storage
        localStorage.setItem(
          this.getFilterDirectory(),
          JSON.stringify(this.state.filterValues)
        );
        this.onRefresh();
      }
    );
  };

  clearFilters = () => {
    this.setState(
      {
        searchValue: '',
        status: 'new',
        source: 'all',
        priority: 'all',
        startDate: null,
        endDate: null,
        filtersControl: [],
        allInOneSelectedIssues: [],
        filterPopoverVisible: false,

        filterValues: {
          status: 'new',
          source: 'all',
          priority: 'all',
          date: [],
        },
      },
      () => {
        const defaultFilters = JSON.stringify({
          status: 'new',
          source: 'all',
          priority: 'all',
          date: [],
          isAllToOne: true,
        });
        localStorage.setItem(this.getFilterDirectory(), defaultFilters);

        const currentUrlParams = new URLSearchParams(window.location.search);
        currentUrlParams.set('issueStatusFilter', 'new');
        currentUrlParams.set('source', 'all');
        currentUrlParams.delete('priorityFilter');
        this.props.history.push(
          window.location.pathname + '?' + currentUrlParams.toString()
        );

        IssuesTableCacheStore.setData('source', 'all');
        IssuesTableCacheStore.setData('priority', 'all');
        IssuesTableCacheStore.setData('status', 'new');
        this.applyFilters();
        this.onRefresh();
      }
    );
  };

  onSelectIssues = (selectedIssues, table) => {
    if (!table.selectedIssues) {
      table.selectedIssues = [];
    }
    // remove from current selectedIssues those that are not selected
    table.selectedIssues = _.filter(table.selectedIssues, (issueId) =>
      _.find(selectedIssues, (i) => i === issueId)
    );
    table.selectedIssues = table.selectedIssues.concat(selectedIssues);
    // remove duplicates
    table.selectedIssues = _.uniq(table.selectedIssues);
    this.forceUpdate();
  };

  onSelectIssuesAllInOneTable = (selectedIssues) => {
    this.setState({
      allInOneSelectedIssues: selectedIssues,
    });
  };

  hydrateSelectedIssues = (selectedIssuesIds) => {
    const hydratedIssues = selectedIssuesIds
      .map((id) => {
        const issue = IssueStore.data.get(id);
        return issue
          ? {
              ...issue,
              key: id,
              name: issue.getName(),
              priorityTagColor: issue.priorityTagColor,
              priorityCategory: issue.priorityCategory,
            }
          : null;
      })
      .filter(Boolean);

    return hydratedIssues;
  };

  async downloadCsv() {
    const params = {
      searchValue: this.state.searchValue,
      status: this.issueStatusCache,
    };
    if (this.issueSourceCache !== 'all') {
      params.source = convertSource(this.issueSourceCache);
    }
    if (this.issuePriorityCache !== 'all') {
      params.priority = this.issuePriorityCache;
    }
    if (this.issueSourceCache === 'dtc') {
      params.includeHistory = true;
    }
    try {
      this.setState({ processingDownloadCsv: true });
      await this.props.downloadCsv(
        params,
        this.state.startDate,
        this.state.endDate
      );
      this.setState({ processingDownloadCsv: false });
    } catch (e) {
      this.setState({ processingDownloadCsv: false });
    }
  }

  addFilterToControl = (type) => {
    let { filtersControl } = this.state;
    filtersControl = _.uniq([...filtersControl, type]);
    this.setState({ filtersControl });
  };

  removeFilterFromControl = (type) => {
    let { filtersControl } = this.state;
    filtersControl = _.filter(filtersControl, (filter) => filter !== type);
    this.setState({ filtersControl });
  };

  handlePopoverVisibleChange = (visible) => {
    this.setState({
      filterPopoverVisible: visible,
    });
  };

  handleDownloadClick = ({ key }) => {
    if (key === 'csv') {
      this.downloadCsv();
    }
  };

  refreshVehiclesList = () => {
    // just send to props if list has changed
    if (this.props.onIssuesListChange) {
      this.props.onIssuesListChange();
    }

    this.onRefresh();
  };

  openCreatedWorkOrderDetails = (workOrder) => {
    this.props.history.push(`/work-order/${workOrder.id}/edit`);
  };

  handlePrintPDF = () => {
    if (this.printedComponentRef.current) {
      PrintService.printElementToPDF(
        this.printedComponentRef.current,
        'issues-report.pdf'
      );
    }
  };

  onRefresh = () => {
    try {
      const currentStore = this.allIssuesTableStore;
      this.props.fetchData(
        {
          offset: 0,
          limit: currentStore.limit || this.props.limit,
          filter: {
            searchValue: this.state.searchValue,
            status: this.issueStatusCache,
            ...(this.issueSourceCache !== 'all' && {
              source: convertSource(this.issueSourceCache),
            }),
            ...(this.issuePriorityCache !== 'all' && {
              priority: this.issuePriorityCache,
            }),
          },
          sort: currentStore.sort || '-priority',
        },
        currentStore,
        this.state.startDate,
        this.state.endDate
      );
    } catch (e) {
      console.log('loaded error', e);
    }
  };

  render() {
    const { searchValue, isAllToOne, startDate, endDate, filterValues } =
      this.state;
    const { isEnabled } = this.props;

    const { device } = deviceStore;

    const selectedIssues = _.get(this.state, 'allInOneSelectedIssues', []);
    const allInOneRowSelectionSettings = {
      selectedRowKeys: selectedIssues,
      onChange: this.onSelectIssuesAllInOneTable,
      hideDefaultSelections: true,
    };
    return (
      <Row gutter={[24, 24]}>
        <Col span={24} ref={this.printedComponentRef}>
          <Row gutter={[16, 16]}>
            <Col
              span={24}
              dir={device !== 'desktop' ? 'vertical' : 'horizontal'}
            >
              <SearchWrapper>
                <Popover
                  placement="bottomLeft"
                  open={this.state.filterPopoverVisible}
                  onOpenChange={this.handlePopoverVisibleChange}
                  content={
                    <div>
                      <div>
                        <LabelSpan>Status</LabelSpan>
                        <RadioGroup
                          onChange={(e) =>
                            this.onChangeFilter('status', e.target.value)
                          }
                          value={this.state.filterValues.status}
                        >
                          <RadioButton value="waitingForApproval">
                            Waiting For Invoice
                          </RadioButton>
                          <RadioButton value="new">Current</RadioButton>
                          <RadioButton value="upcoming">Upcoming</RadioButton>
                          <RadioButton value="done">Done</RadioButton>
                        </RadioGroup>
                      </div>

                      <FilterItemWrapper>
                        <LabelSpan>Filter by a Source</LabelSpan>
                        <Select
                          showSearch
                          placeholder="Filter by a Source"
                          style={{ width: '100%' }}
                          onChange={(val) => this.onChangeFilter('source', val)}
                          value={this.state.filterValues.source}
                        >
                          <Option value="all">All</Option>
                          <Option value="faultCode">
                            Only Engine Fault Code
                          </Option>
                          <Option value="pitstopSuggested">
                            Only Pitstop Suggested
                          </Option>
                          <Option value="routine">
                            Only Routine Based Services{' '}
                          </Option>
                          <Option value="recall">Only Recalls</Option>
                          <Option value="user">Reported by User</Option>
                          <Option value="archive">Service Center</Option>
                        </Select>
                      </FilterItemWrapper>

                      <FilterItemWrapper>
                        <LabelSpan>Filter by Priority</LabelSpan>
                        <Select
                          showSearch
                          placeholder="Filter by Priority"
                          style={{ width: '100%' }}
                          onChange={(val) =>
                            this.onChangeFilter('priority', val)
                          }
                          value={this.state.filterValues.priority}
                        >
                          <Option value="all">All</Option>
                          <Option value="critical">Only Critical</Option>
                          <Option value="major">Only Major</Option>
                          <Option value="minor">Only Minor</Option>
                        </Select>
                      </FilterItemWrapper>

                      {this.props.history.location.pathname === '/issues' && (
                        <FilterItemWrapper>
                          <LabelSpan>Filter by Date</LabelSpan>
                          <RangePicker
                            style={{ width: '100%' }}
                            value={
                              filterValues.date?.length > 0
                                ? [
                                    dayjs(filterValues.date[0], 'YYYY-MM-DD'),
                                    dayjs(filterValues.date[1], 'YYYY-MM-DD'),
                                  ]
                                : []
                            }
                            onChange={(date) => {
                              const formattedDate =
                                date && date.length === 2
                                  ? date.map((d) =>
                                      d ? d.format('YYYY-MM-DD') : null
                                    )
                                  : [];
                              this.onChangeFilter('date', formattedDate);
                            }}
                          />
                        </FilterItemWrapper>
                      )}

                      <FilterActions>
                        <Button onClick={this.clearFilters}>Clear</Button>
                        <Button type="primary" onClick={this.applyFilters}>
                          Apply
                        </Button>
                      </FilterActions>
                    </div>
                  }
                  trigger="click"
                >
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    className="filter-button"
                  >
                    Filters
                    {this.state.filtersControl.length > 0 ? (
                      <FilterCountBadge>
                        {this.state.filtersControl.length}
                      </FilterCountBadge>
                    ) : (
                      <></>
                    )}
                  </Button>
                </Popover>
                <SearchIssue
                  car={this.props.car}
                  onSearch={(val) => {
                    this.setState(
                      {
                        searchOn: val.searchOn,
                        searchValue: val.searchValue,
                      },
                      () => {
                        // Trigger data refresh with new search parameters
                        if (this.state.isAllToOne && this.allIssuesTableStore) {
                          const currentStore = this.allIssuesTableStore;

                          this.props.fetchData(
                            {
                              offset: 0,
                              limit: currentStore.limit || this.props.limit,
                              filter: {
                                searchValue: val.searchValue,
                                status: this.issueStatusCache,
                                ...(this.issueSourceCache !== 'all' && {
                                  source: convertSource(this.issueSourceCache),
                                }),
                                ...(this.issuePriorityCache !== 'all' && {
                                  priority: this.issuePriorityCache,
                                }),
                              },
                              sort: currentStore.sort || '-priority',
                            },
                            currentStore,
                            this.state.startDate,
                            this.state.endDate
                          );
                        }
                      }
                    );
                  }}
                  showDownloadButton={this.props.car ? true : false}
                />
              </SearchWrapper>
            </Col>
          </Row>

          {this.props.history.location.pathname === '/issues' &&
            !ShopStore.isAdmin() && (
              <DownloadReportWrapper>
                {/* <ReactToPrint
                  trigger={() => {
                    return <PitstopButton>Download PDF report</PitstopButton>;
                  }}
                  content={() => this.printedComponentRef}
                />
                <PitstopButton className="dowload-csv" loading={this.state.processingDownloadCsv} onClick={this.downloadCsv}>Download CSV</PitstopButton> */}
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'pdf',
                        label: (
                          <PDFButton type="link" onClick={this.handlePrintPDF}>
                            PDF Format
                          </PDFButton>
                        ),
                      },
                      {
                        key: 'csv',
                        label: 'CSV Format',
                      },
                    ],
                    onClick: this.handleDownloadClick,
                  }}
                >
                  <PitstopButton type="primary" ghost>
                    <DownloadOutlined />
                    Download Issues
                  </PitstopButton>
                </Dropdown>
              </DownloadReportWrapper>
            )}

          <>
            {isEnabled && (
              <>
                {!this.isAllStoresLoaded && (
                  <Row style={{ marginTop: 16 }}>
                    <Spin size="large" />
                  </Row>
                )}

                <Row hidden={!this.isAllStoresLoaded} gutter={[16, 16]}>
                  {this.issueSourceCache === 'all' && this.isAllStoresEmpty && (
                    <Empty
                      style={{ width: '100%' }}
                      description={
                        <span style={{ color: '#123fbf' }}>
                          You have no issues
                          <br />
                          (Remove search or filters if applied)
                        </span>
                      }
                    />
                  )}

                  {isAllToOne ? (
                    <>
                      <Col
                        hidden={!this.allIssuesTableStore?.loaded}
                        span={24}
                        style={{ margin: '12px 0px' }}
                      >
                        <TitleWrapper>
                          {this.issueStatusCache === 'new' ? (
                            <ActionsWrapper>
                              <MarkAsCompleteBtn
                                issues={this.hydrateSelectedIssues(
                                  selectedIssues
                                )}
                                onClick={() => {
                                  this.setState({
                                    allInOneSelectedIssues: [],
                                  });
                                  this.refreshVehiclesList();
                                }}
                                title={`Mark Selected as Complete (${selectedIssues.length})`}
                                type="primary"
                                shouldDisableButton={!selectedIssues.length}
                              />
                              {this.props.page === 'carPage' ? (
                                <CreateWorkOrderFromIssuesBtn
                                  issues={this.hydrateSelectedIssues(
                                    selectedIssues
                                  )}
                                  onFinish={(workOrderCreated) => {
                                    this.setState({
                                      allInOneSelectedIssues: [],
                                    });

                                    const jsxMessage = !isEmpty(
                                      workOrderCreated
                                    ) ? (
                                      <div>
                                        The Work Order was created{' '}
                                        <Button
                                          style={{
                                            padding: 0,
                                          }}
                                          type="link"
                                          onClick={() => {
                                            this.openCreatedWorkOrderDetails(
                                              workOrderCreated
                                            );
                                          }}
                                        >
                                          Click Here
                                        </Button>{' '}
                                        to view the details and to make edits
                                      </div>
                                    ) : (
                                      <div>
                                        You can always click on the work order
                                        for more details and to make edits
                                      </div>
                                    );

                                    // if work order was created, show a different message
                                    notification.success({
                                      message: 'Work orders saved',
                                      key: 'orderUpdated',
                                      description: jsxMessage,
                                    });
                                  }}
                                  title={`Create Work Order (${selectedIssues.length})`}
                                  shouldDisableButton={!selectedIssues.length}
                                />
                              ) : (
                                <></>
                              )}
                            </ActionsWrapper>
                          ) : this.issueStatusCache === 'waitingForApproval' ? (
                            <>
                              <MarkAsCompleteBtn
                                issues={this.hydrateSelectedIssues(
                                  selectedIssues
                                )}
                                onClick={() => {
                                  this.setState({
                                    allInOneSelectedIssues: [],
                                  });
                                  this.refreshVehiclesList();
                                }}
                                title={`Mark Selected as Complete (${selectedIssues.length})`}
                                type="primary"
                                shouldDisableButton={!selectedIssues.length}
                              />
                              <MarkAsCurrentBtn
                                issues={this.hydrateSelectedIssues(
                                  selectedIssues
                                )}
                                onClick={() => {
                                  this.setState({
                                    allInOneSelectedIssues: [],
                                  });
                                  this.refreshVehiclesList();
                                }}
                                title={`Move Selected to Current (${selectedIssues.length})`}
                                type="primary"
                                shouldDisableButton={!selectedIssues.length}
                              />
                            </>
                          ) : (
                            <MarkAsCurrentBtn
                              issues={this.hydrateSelectedIssues(
                                selectedIssues
                              )}
                              onClick={() => {
                                this.setState({
                                  allInOneSelectedIssues: [],
                                });
                                this.refreshVehiclesList();
                              }}
                              title={`Move Selected to Current (${selectedIssues.length})`}
                              type="primary"
                              shouldDisableButton={!selectedIssues.length}
                            />
                          )}
                        </TitleWrapper>
                        <PitstopTable
                          ref={this.issuesTableref}
                          id={`${this.props.id}-${this.issueSourceCache}-${this.issueStatusCache}-${this.issuePriorityCache}-issues-x`}
                          data={this.getDataBySource(this.issueSourceCache)}
                          limit={
                            this.allIssuesTableStore?.loaded
                              ? this.allIssuesTableStore?.limit
                              : _.includes(
                                    customFleets.renzi,
                                    ShopStore.currentShop.id
                                  )
                                ? 10
                                : this.props.limit
                          }
                          rowKey="id"
                          columns={getColumnsBySource(
                            this.issueSourceCache,
                            this.props.page
                          )}
                          pagination={{
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '25', '50', '100'],
                            preservePageOnDataChange: true,
                          }}
                          components={{
                            header: {
                              cell: CustomHeaderCell,
                            },
                          }}
                          searchValue={searchValue}
                          timeRange={{ startDate, endDate }}
                          fetchData={(params, store) => {
                            let sort = params.sort;
                            if (typeof sort === 'undefined') {
                              sort = '-priority';
                            }
                            const _params = {
                              ...params,
                              includeHistory: this.issueSourceCache === 'dtc',
                              filter: {
                                ...params.filter,
                                searchValue,
                                status: this.issueStatusCache,
                                ...(this.issueSourceCache !== 'all' && {
                                  source: convertSource(this.issueSourceCache),
                                }),
                                ...(this.issuePriorityCache === 'all'
                                  ? delete store?.filter['priority']
                                  : { priority: this.issuePriorityCache }),
                              },
                              sort,
                            };

                            if (this.issueSourceCache === 'dtc') {
                              _params.includeHistory = true;
                            }

                            // Store the current filter params to help with hydration
                            store.currentParams = _params;

                            this.props.fetchData(
                              {
                                ..._params,
                              },
                              store,
                              startDate,
                              endDate
                            );
                          }}
                          getTableStore={(tableStore) => {
                            this.allIssuesTableStore = tableStore;
                            // Ensure we have the data in the store
                            if (tableStore && !tableStore.data) {
                              tableStore.data = [];
                            }
                            setTimeout(() => this.forceUpdate(), 0);
                          }}
                          onRowClick={(record) => {
                            if (
                              record.source === 'dtc' ||
                              record.source === 'spn'
                            ) {
                              return (
                                record.id &&
                                this.props.history.push(
                                  `/issues/${record.id}/v2`
                                )
                              );
                            }
                            return (
                              record.id &&
                              this.props.history.push(`/issues/${record.id}`)
                            );
                          }}
                          rowSelection={{
                            ...allInOneRowSelectionSettings,
                            preserveSelectedRowKeys: true,
                            selectedRowKeys: this.state.allInOneSelectedIssues,
                            onChange: (selectedRowKeys) => {
                              this.onSelectIssuesAllInOneTable(selectedRowKeys);
                            },
                          }}
                          scroll={{ x: 240 }}
                        />
                      </Col>
                    </>
                  ) : (
                    <>
                      {_.map(this.allTableSources, (table) => {
                        const tableId = `${this.props.id}-${table.source}-${this.issueStatusCache}-${this.issuePriorityCache}-issues`;

                        const selectedIssues = table?.selectedIssues || [];
                        const rowSelectionSettings = {
                          selectedRowKeys: selectedIssues,
                          onChange: (selectedKeys) =>
                            this.onSelectIssues(selectedKeys, table),
                          hideDefaultSelections: true,
                        };

                        return (
                          <Col
                            hidden={
                              convertSource(this.issueSourceCache) !==
                                table.source &&
                              table.store.loaded &&
                              _.isEmpty(this.getDataBySource(table.source))
                            }
                            key={table.source}
                            span={24}
                          >
                            <TitleWrapper>
                              <h5>{table.title}</h5>
                              {this.issueStatusCache === 'new' ? (
                                <ActionsWrapper>
                                  <MarkAsCompleteBtn
                                    issues={this.hydrateSelectedIssues(
                                      selectedIssues
                                    )}
                                    onClick={() => {
                                      this.setState({
                                        allInOneSelectedIssues: [],
                                      });
                                      this.refreshVehiclesList();
                                    }}
                                    title={`Mark Selected as Complete (${selectedIssues.length})`}
                                    type="primary"
                                    shouldDisableButton={!selectedIssues.length}
                                  />
                                  {this.props.page === 'carPage' ? (
                                    <CreateWorkOrderFromIssuesBtn
                                      issues={this.hydrateSelectedIssues(
                                        selectedIssues
                                      )}
                                      onFinish={() => {
                                        this.setState({
                                          allInOneSelectedIssues: [],
                                        });
                                      }}
                                      title={`Create Work Order (${selectedIssues.length})`}
                                      type="secondary"
                                      shouldDisableButton={
                                        !selectedIssues.length
                                      }
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </ActionsWrapper>
                              ) : this.issueStatusCache ===
                                'waitingForApproval' ? (
                                <>
                                  <MarkAsCompleteBtn
                                    issues={this.hydrateSelectedIssues(
                                      selectedIssues
                                    )}
                                    onClick={() => {
                                      this.setState({
                                        allInOneSelectedIssues: [],
                                      });
                                      this.refreshVehiclesList();
                                    }}
                                    title={`Mark Selected as Complete (${selectedIssues.length})`}
                                    type="primary"
                                    shouldDisableButton={!selectedIssues.length}
                                  />
                                  <MarkAsCurrentBtn
                                    issues={this.hydrateSelectedIssues(
                                      selectedIssues
                                    )}
                                    onClick={() => {
                                      this.setState({
                                        allInOneSelectedIssues: [],
                                      });
                                      this.refreshVehiclesList();
                                    }}
                                    title={`Move Selected to Current (${selectedIssues.length})`}
                                    type="primary"
                                    shouldDisableButton={!selectedIssues.length}
                                  />
                                </>
                              ) : (
                                <MarkAsCurrentBtn
                                  issues={this.hydrateSelectedIssues(
                                    selectedIssues
                                  )}
                                  onClick={() => {
                                    this.setState({
                                      allInOneSelectedIssues: [],
                                    });
                                    this.refreshVehiclesList();
                                  }}
                                  title={`Move Selected to Current (${selectedIssues.length})`}
                                  type="primary"
                                  shouldDisableButton={!selectedIssues.length}
                                />
                              )}
                            </TitleWrapper>

                            <PitstopTable
                              id={tableId}
                              data={this.getDataBySource(table.source)}
                              limit={
                                table.store.loaded
                                  ? table.store.limit
                                  : this.props.limit
                              }
                              rowKey={(record) => record.id}
                              columns={getColumnsBySource(
                                table.source,
                                this.props.page
                              )}
                              pagination={{
                                showSizeChanger: true,
                                pageSizeOptions: ['5', '10', '25', '50', '100'],
                                preservePageOnDataChange: true,
                              }}
                              searchValue={searchValue}
                              timeRange={{ startDate, endDate }}
                              fetchData={(params, store) => {
                                const _params = {
                                  ...params,
                                  includeHistory: table.source === 'dtc',
                                  filter: {
                                    ...params.filter,
                                    searchValue,
                                    status: this.issueStatusCache,
                                    source: table.source,
                                    ...(this.issuePriorityCache === 'all'
                                      ? delete store.filter['priority']
                                      : {
                                          priority: this.issuePriorityCache,
                                        }),
                                  },
                                  sort: params.sort || '-priority',
                                };

                                if (table.source === 'dtc') {
                                  _params.includeHistory = true;
                                }

                                // Store the current filter params to help with hydration
                                store.currentParams = _params;

                                this.props.fetchData(
                                  {
                                    ..._params,
                                  },
                                  store,
                                  startDate,
                                  endDate
                                );
                              }}
                              getTableStore={(tableStore) =>
                                table.setStore(tableStore)
                              }
                              onRowClick={(record) => {
                                if (
                                  record.id &&
                                  ['dtc', 'spn'].includes(record.source)
                                ) {
                                  this.props.history.push(
                                    `/issues/${record.id}/v2`
                                  );
                                } else if (record.id) {
                                  this.props.history.push(
                                    `/issues/${record.id}`
                                  );
                                }
                              }}
                              rowSelection={rowSelectionSettings || null}
                            />
                          </Col>
                        );
                      })}
                    </>
                  )}
                </Row>
              </>
            )}
          </>
        </Col>
      </Row>
    );
  }
}

export default withRouter(observer(IssuesTable));
